<template>
  <div v-if="props.summary" class="future-content-section-wrap">
    <h2 v-if="props.showTitleAboveSection" class="global-h2 future-content-section-title__top">{{ props.title }}</h2>
    <div
      class="future-content-section"
      :class="{
        'future-content-section--good': contentNotNeeded,
        'future-content-section--needs-content': !contentNotNeeded && !isCritical,
        'future-content-section--critical': isCritical,
        'future-content-section--collapsed': isCollapsed,
      }"
    >
      <div class="future-content-section__header" @click="toggleCollapse">
        <IconEmbedded :name="isCollapsed ? 'caret-bottom_4-5' : 'caret-top_4-5'" :size="24" class="future-content-section__toggle-icon" />
        <h2 v-if="!props.showTitleAboveSection" class="global-h2 future-content-section-title">{{ props.title }}</h2>
        <div v-if="needLevel" class="future-content-section__status-pill" :data-status="needLevel">
          <span class="future-content-section__status-dot"></span>
          {{ statusLabel }}
        </div>
      </div>

      <div v-if="!isCollapsed" class="future-content-section__content">
        <div class="future-content-section__body-col">
          <div v-if="props.showTopicSummary && props.summary.topicSummary" class="future-content-section__topic-summary">
            <p class="global-p">{{ props.summary.topicSummary }}</p>
          </div>

          <div v-if="props.summary.moreContentGuidance" class="future-content-section__more-content-guidance">
            <h4 class="future-content-section__label">{{ actionNeeded ? "What Content is Needed?" : "Content Considerations" }}</h4>
            <p class="global-p">{{ props.summary.moreContentGuidance }}</p>
          </div>
        </div>

        <div class="future-content-section__actions-col">
          <h4 class="future-content-section__actions-heading">{{ actionsHeading }}</h4>
          <div class="future-content-section__actions">
            <SrpButton
              v-if="props.actionsShown.includes('customerUpload')"
              size="small"
              :color="actionNeeded ? 'teal' : 'gray'"
              :fill="actionNeeded ? 'solid' : 'outlined'"
              @click="emit('action', 'customerUpload', props.bucketKey)"
            >
              <template #icon><IconEmbedded name="upload_2" :size="16" /></template>
              Upload Content
            </SrpButton>

            <SrpButton
              v-if="props.actionsShown.includes('communityUpload')"
              size="small"
              :color="actionNeeded ? 'teal' : 'gray'"
              :fill="actionNeeded ? 'solid' : 'outlined'"
              @click="emit('action', 'communityUpload', props.bucketKey)"
            >
              <template #icon><IconEmbedded name="users_2" :size="16" /></template>
              Create a Community Upload
            </SrpButton>

            <SrpButton
              v-if="props.actionsShown.includes('creatorVisit')"
              size="small"
              :color="actionNeeded ? 'teal' : 'gray'"
              :fill="actionNeeded ? 'solid' : 'outlined'"
              @click="emit('action', 'setupCreatorVisit', props.bucketKey)"
            >
              <template #icon><IconEmbedded name="user_2" :size="16" /></template>
              Set up a Creator Visit
            </SrpButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { CalendarGenResultSummaryData } from "@contracts/contentCalendar";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

export type FutureContentAction = "customerUpload" | "communityUpload" | "creatorVisit";

const props = defineProps<{
  summary: CalendarGenResultSummaryData | null;
  title: string;
  showTopicSummary: boolean;
  showTitleAboveSection: boolean;
  actionsShown: Array<FutureContentAction>;
  bucketKey: string;
}>();

const emit = defineEmits<{
  (e: "action", actionKey: string, bucketKey: string): void;
}>();

type BackendNeedLevel = "none" | "low" | "medium" | "high";

// Extract need level from backend data
function getBackendNeedLevel(summary: CalendarGenResultSummaryData | null): BackendNeedLevel | null {
  if (!summary?.moreContentNeeded) return null;
  const level = summary.moreContentNeeded.toLowerCase();
  if (["none", "low", "medium", "high"].includes(level)) {
    return level as BackendNeedLevel;
  }
  return null;
}

// Get status label for display
function getStatusLabel(needLevel: BackendNeedLevel | null): string {
  const labels: Record<BackendNeedLevel, string> = {
    none: "Content Looks Great",
    low: "Content Looks Good",
    medium: "More Content Recommended",
    high: "Content Needed to Continue!",
  };
  return needLevel ? labels[needLevel] : "";
}

// Get actions heading based on need level
function getActionsHeading(actionNeeded: boolean): string {
  if (actionNeeded) {
    return "Action Needed";
  }
  return "Suggested Action";
}

const needLevel = computed(() => getBackendNeedLevel(props.summary));
const statusLabel = computed(() => getStatusLabel(needLevel.value));
const actionsHeading = computed(() => getActionsHeading(actionNeeded.value));
const actionNeeded = computed(() => {
  const level = needLevel.value;
  return level === "medium" || level === "high";
});
const contentNotNeeded = computed(() => {
  const level = needLevel.value;
  return level === "none" || level === "low";
});
const isCritical = computed(() => {
  const level = needLevel.value;
  return level === "high";
});

// Collapsed state - default to collapsed if content is not needed
const isCollapsed = ref(contentNotNeeded.value);

function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value;
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/variables.scss";

.future-content-section-wrap {
  margin-bottom: 18px;
}

.future-content-section-title__top {
  margin-bottom: 10px;
}

.future-content-section {
  border-radius: 16px;
  background-color: #ffffff;
  padding: 18px;
  box-shadow: 0 10px 24px rgba(13, 40, 50, 0.04);
  position: relative;
  z-index: 0;

  &::before {
    content: "";
    width: calc(100% + 60px);
    height: 100%;
    border: 2px solid #d4e2dd;
    border-radius: 16px;
    background-color: #ffffff;
    position: absolute;
    inset: 0 auto auto -30px;
    z-index: -1;
  }

  &--collapsed {
    padding: 18px;
  }
}

.future-content-section--good {
  &::before {
    border-left: 4px solid #0c8a7b;
  }
}

.future-content-section--needs-content {
  &::before {
    border-left: 4px solid #f4b000;
  }
}

.future-content-section--critical {
  &::before {
    border-left: 4px solid #d32f2f;
  }
}

.future-content-section__header {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
  margin-left: -35px;
  margin-bottom: 16px;

  .future-content-section--collapsed & {
    margin-bottom: 0;
  }
}

.future-content-section__toggle-icon {
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.5);
  transition: transform 0.2s ease;
}

.future-content-section__content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
}

.future-content-section__body-col {
  flex: 2 1 0;
}

.future-content-section__actions-col {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.future-content-section__status-pill {
  @include standardFont;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  margin: 0;
}

.future-content-section__status-pill[data-status="none"] {
  background-color: #e3f3f0;
  color: #0c8a7b;
}

.future-content-section__status-pill[data-status="low"] {
  background-color: #e3f3f0;
  color: #0c8a7b;
}

.future-content-section__status-pill[data-status="medium"] {
  background-color: #fff3e3;
  color: #9a5a17;
}

.future-content-section__status-pill[data-status="high"] {
  background-color: #ffebee;
  color: #c62828;
}

.future-content-section__status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: currentColor;
}

.future-content-section__topic-summary {
  margin-bottom: 15px;
}

.future-content-section__label {
  /* @include standardFont;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;*/
  letter-spacing: 0.05em;
  margin-bottom: 3px;
}

.future-content-section__actions-heading {
  @include standardFont;
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.7;
}

.future-content-section__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
}

// Responsive adjustments
@media (max-width: $tablet-max-width) {
  .future-content-section {
    padding: 16px;

    &--collapsed {
      padding: 16px;
    }
  }

  .future-content-section__header {
    margin-bottom: 12px;
    gap: 8px;

    .future-content-section--collapsed & {
      margin-bottom: 0;
    }
  }

  .future-content-section__content {
    flex-direction: column;
    gap: 12px;
  }

  .future-content-section__actions-col {
    align-items: flex-start;
  }

  .future-content-section__actions {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
  }
}
</style>
